import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Clock, MapPin, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { diffFields, fieldLabel } from "@/components/BuyerCriteriaRequestsPanel";
import { DetailRequestRow, useDetailRequests } from "@/components/buyer/DetailRequestRow";
import { inboxChanged } from "@/components/layout/useInboxCount";

const BTN = "border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 disabled:opacity-40";

export function stateChangeText(row, current = (row.current_criteria_snapshot || {})) {
  const prop = row.proposed_criteria || {};
  if (!("accepted_states" in prop)) return null;
  const cur = [...(current.accepted_states || [])].sort();
  const next = [...(prop.accepted_states || [])].sort();
  if (cur.join() === next.join()) return null;
  if (!next.length) return "Accepted states: every state";
  if (!cur.length) return `Accepted states: only ${next.join(", ")}`;
  const added = next.filter((s) => !cur.includes(s));
  const removed = cur.filter((s) => !next.includes(s));
  if (removed.length > next.length) return `Accepted states: only ${next.join(", ")}`;
  return `Accepted states: ${[added.length && `adds ${added.join(", ")}`, removed.length && `removes ${removed.join(", ")}`].filter(Boolean).join("; ")}`;
}

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  const where = (d?.broadening_destinations || []).map((x) => `${x.destination_name}: ${x.extra_states.join(", ")}`).join("; ");
  return d?.message ? `${d.message}${where ? ` (${where})` : ""}` : "Couldn't update the request";
}

function RequestRow({ row, onDone }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const live = row.live_criteria || {};
  const states = stateChangeText(row, live);
  const diffs = diffFields(live, row.proposed_criteria);
  const others = diffs.filter((d) => d.key !== "accepted_states").map((d) => fieldLabel(d.key));
  const p = `dash-buyer-request-${row.id}`;
  const act = async (kind, body, ok) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/buyer-criteria-requests/${row.id}/${kind}`, body);
      if (data.email_sent) toast.success(`${ok}. The buyer was emailed.`);
      else toast.warning(`${ok}, but the email to the buyer didn't go through.`);
      inboxChanged();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-b border-black p-3 last:border-b-0" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <b data-testid={`${p}-buyer`}>{row.client_org_name || "Buyer"}</b>
        <span className="text-zinc-500">{row.requester_name}</span>
        <span className="font-mono text-[10px] text-zinc-500">{new Date(row.created_at).toLocaleString()}</span>
        <Link to={`/clients/${row.client_org_id}/manage`} className="ml-auto text-[10px] uppercase tracking-widest underline" data-testid={`${p}-open`}>Open buyer</Link>
      </div>
      {states && <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold" data-testid={`${p}-states`}><MapPin size={14} weight="bold" className="shrink-0" />{states}</div>}
      {others.length > 0 && <div className="mt-1 text-[11px] text-zinc-700" data-testid={`${p}-others`}>{states ? "Also changes" : "Changes"}: {others.join(", ")}</div>}
      {!diffs.length && <div className="mt-1 text-[11px] italic text-zinc-500">No changes compared to the current criteria.</div>}
      {row.notes && <div className="mt-2 border-l-4 border-amber-500 bg-amber-50 p-2 text-xs whitespace-pre-wrap" data-testid={`${p}-notes`}>{row.notes}</div>}
      {rejecting ? (
        <div className="mt-2 space-y-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Short reason the buyer will see"
                    className="w-full border border-black p-2 text-xs" data-testid={`${p}-reason`} />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy || !reason.trim()} onClick={() => act("reject", { review_notes: reason.trim() }, "Request rejected")}
                    className={`${BTN} bg-rose-600 text-white`} data-testid={`${p}-reject-confirm`}><XCircle size={12} weight="bold" /> Reject</button>
            <button type="button" onClick={() => setRejecting(false)} className={`${BTN} hover:bg-black hover:text-white`} data-testid={`${p}-reject-cancel`}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" disabled={busy || !diffs.length} onClick={() => act("approve", { review_notes: null }, "Approved and live")}
                  className={`${BTN} bg-emerald-700 text-white`} data-testid={`${p}-approve`}>
            <CheckCircle size={12} weight="bold" /> {busy ? "Applying…" : "Approve"}
          </button>
          <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={`${BTN} hover:bg-black hover:text-white`} data-testid={`${p}-reject`}>
            <XCircle size={12} weight="bold" /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

// Every pending buyer criteria request, state changes first, approvable in one tap.
function AlertSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [email, setEmail] = useState("");
  const [changeTo, setChangeTo] = useState("all");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/buyer-alert-settings").then((r) => {
      setS(r.data);
      setEmail(r.data.extra_email);
      setChangeTo(r.data.change_alert_to === "all" ? "all" : r.data.change_alert_to === user?.email?.toLowerCase() ? "me" : "other");
    }).catch(() => setS(false));
  }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/buyer-alert-settings", { extra_email: email, ...(changeTo === "other" ? {} : { change_alert_to: changeTo }) });
      setS(data);
      toast.success("Alert address saved");
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-b border-black px-3 py-2 text-[11px]" data-testid="dash-buyer-alerts">
      <button type="button" onClick={() => setOpen((v) => !v)} className="underline" data-testid="dash-buyer-alerts-toggle">
        Email alerts go to {s.recipients.length} {s.recipients.length === 1 ? "person" : "people"}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="text-zinc-600" data-testid="dash-buyer-alerts-list">
            Every admin who manages buyers gets an email when a buyer sends a change or a delivery check fails: {s.recipients.join(", ") || "nobody yet"}.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="dash-alert-email">Also send to</label>
            <input id="dash-alert-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com"
                   className="min-w-0 flex-1 border border-black px-2 py-1 text-xs" data-testid="dash-buyer-alerts-email" />
            <button type="button" onClick={save} disabled={busy} className={`${BTN} bg-black text-white`} data-testid="dash-buyer-alerts-save">Save</button>
          </div>
          <div className="space-y-1" data-testid="dash-buyer-change-alerts">
            <div>Emails when a buyer&apos;s states, pages or lead rules change go to:</div>
            {[
              ["all", "Everyone on this list"],
              ["me", `Only me (${user?.email || "you"})`],
              ...(s.change_alert_to !== "all" && s.change_alert_to !== user?.email?.toLowerCase() ? [["other", `Only ${s.change_alert_to} (current)`]] : []),
            ].map(([v, label]) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="change-alert-to" className="accent-black" checked={changeTo === v} onChange={() => setChangeTo(v)}
                       data-testid={`dash-buyer-change-alerts-${v}`} />
                {label}
              </label>
            ))}
            <div className="text-zinc-500" data-testid="dash-buyer-change-alerts-to">Going to: {s.change_recipients.join(", ") || "nobody yet"}. Press Save after changing this.</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function BuyerRequestsCard() {
  const [rows, setRows] = useState(null);
  const [details, loadDetails] = useDetailRequests();
  const load = useCallback(() => {
    api.get("/admin/buyer-criteria-requests", { params: { status: "pending" } })
      .then((r) => setRows(r.data.requests || []))
      .catch(() => setRows(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return null;
  const waiting = rows.length + details.length;
  const sorted = [...rows].sort((a, b) => Number(Boolean(stateChangeText(b, b.live_criteria || {}))) - Number(Boolean(stateChangeText(a, a.live_criteria || {}))));
  return (
    <div className="brut-card mb-6" data-testid="dash-buyer-requests">
      <div className="flex flex-wrap items-center gap-2 border-b border-black p-3">
        <Clock size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Buyer requests</div>
        <span className="label-mono ml-auto" data-testid="dash-buyer-requests-count">{waiting} waiting</span>
        <Link to="/admin/requests" className="text-[10px] uppercase tracking-widest underline" data-testid="dash-buyer-requests-inbox">Open request inbox</Link>
      </div>
      <AlertSettings />
      {details.map((r) => <DetailRequestRow key={r.id} row={r} onDone={loadDetails} />)}
      {sorted.map((r) => <RequestRow key={r.id} row={r} onDone={load} />)}
      {!waiting && (
        <div className="p-3 text-[11px] text-zinc-600" data-testid="dash-buyer-requests-empty">No buyer requests waiting.</div>
      )}
    </div>
  );
}
